import { useAccount } from "wagmi";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import tokenService from "services/token.service";
import { useAuth } from "./useAuth";
import { getLoggingService } from "../services/logging.service";
import { LogAction, WalletType } from "../types/logging.types";
import {
  ASSOCIATION_API_BASE,
  ASSOCIATION_HTTP_STATUS,
  ASSOCIATION_MESSAGES,
} from "../constants/walletAssociation.constants";

export interface WalletAssociationState {
  isAssociated: boolean;
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;
  checkAssociation: () => Promise<boolean>;
  checkAssociationAndAutoAssociate: () => Promise<void>;
}

export interface UseWalletAssociationConfig {
  /** Whether this IDO requires the chain association at all. */
  requires: boolean;
  /** Chain label used for logging. */
  chain: WalletType;
  /** Address of the connected non-EVM wallet (null when disconnected). */
  walletAddress: string | null;
  /** Whether the non-EVM wallet is connected. */
  connected: boolean;
  /** Body field name expected by the backend (e.g. "solanaAddress", "tonAddress"). */
  addressField: string;
  /** Base route for this chain's association endpoints. */
  route: string;
}

const buildAuthHeaders = () => ({
  Authorization: `Bearer ${tokenService.tokens?.accessToken}`,
});

/**
 * Generic EVM <-> non-EVM wallet association state machine: checks for an
 * existing association and auto-creates one when missing. Chain-specific hooks
 * (Solana, TON) wrap this with their own address source and route.
 */
export const useWalletAssociation = ({
  requires,
  chain,
  walletAddress,
  connected,
  addressField,
  route,
}: UseWalletAssociationConfig): WalletAssociationState => {
  const { address: evmAddress } = useAccount();
  const isAuth = useAuth();
  const [isAssociated, setIsAssociated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastCallRef = useRef<string>("");
  const isCheckingRef = useRef(false);
  const logger = getLoggingService();

  const isReady = Boolean(walletAddress && evmAddress && isAuth);

  const checkAssociation = useCallback(async () => {
    if (!isReady || !walletAddress || !evmAddress) {
      setIsAssociated(false);
      return false;
    }

    const callKey = `${evmAddress}-${walletAddress}`;
    if (lastCallRef.current === callKey && isCheckingRef.current) {
      return isAssociated;
    }
    if (lastCallRef.current === callKey && isAssociated) {
      return true;
    }

    lastCallRef.current = callKey;
    isCheckingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${ASSOCIATION_API_BASE}${route}/${evmAddress}`,
        { headers: buildAuthHeaders() }
      );
      const result: boolean = response.data.isAssociated || false;

      logger.logWalletConnection(chain, LogAction.ASSOCIATION_CHECK, {
        evmAddress,
        walletAddress,
        isAssociated: result,
      });

      setIsAssociated(result);
      return result;
    } catch (err: any) {
      logger.logConnectionError(
        chain,
        err instanceof Error ? err : new Error("Association check failed"),
        { evmAddress, walletAddress, statusCode: err.response?.status, phase: "association_check" }
      );

      setIsAssociated(false);
      setError(
        err.response?.status === ASSOCIATION_HTTP_STATUS.NOT_FOUND
          ? ASSOCIATION_MESSAGES.CHECK_SERVICE_UNAVAILABLE
          : err.response?.data?.message || ASSOCIATION_MESSAGES.CHECK_FAILED
      );
      return false;
    } finally {
      isCheckingRef.current = false;
      setIsLoading(false);
    }
  }, [isReady, walletAddress, evmAddress, isAssociated, route, chain, logger]);

  const checkAssociationAndAutoAssociate = useCallback(async () => {
    if (!isReady || !walletAddress || !evmAddress) {
      setIsAssociated(false);
      return;
    }

    const alreadyAssociated = await checkAssociation();
    if (alreadyAssociated) {
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const response = await axios.post(
        `${ASSOCIATION_API_BASE}${route}`,
        { evmAddress, [addressField]: walletAddress },
        { headers: buildAuthHeaders() }
      );

      if (response.status === ASSOCIATION_HTTP_STATUS.CREATED) {
        logger.logConnectionSuccess(chain, walletAddress, {
          action: "association_creation",
          evmAddress,
          walletAddress,
          associationCreated: true,
        });
        setIsAssociated(true);
        setError(null);
      }
    } catch (err: any) {
      logger.logConnectionError(
        chain,
        err instanceof Error ? err : new Error("Association creation failed"),
        { evmAddress, walletAddress, statusCode: err.response?.status, phase: "association_creation" }
      );

      const status = err.response?.status;
      if (status === ASSOCIATION_HTTP_STATUS.CONFLICT) {
        // Association already exists (created elsewhere) — treat as success.
        setIsAssociated(true);
        setError(null);
      } else if (status === ASSOCIATION_HTTP_STATUS.NOT_FOUND) {
        setError(ASSOCIATION_MESSAGES.CREATE_SERVICE_UNAVAILABLE);
      } else if (status === ASSOCIATION_HTTP_STATUS.BAD_REQUEST) {
        setError(ASSOCIATION_MESSAGES.INVALID_ADDRESS);
      } else {
        setError(err.response?.data?.message || ASSOCIATION_MESSAGES.CREATE_FAILED);
      }
    } finally {
      setIsCreating(false);
    }
  }, [isReady, walletAddress, evmAddress, addressField, route, chain, logger, checkAssociation]);

  useEffect(() => {
    if (!requires) {
      setIsAssociated(false);
      setError(null);
      return;
    }

    if (connected && isReady) {
      void checkAssociationAndAutoAssociate();
    } else {
      setIsAssociated(false);
      setError(null);
      lastCallRef.current = "";
    }
  }, [requires, connected, isReady, checkAssociationAndAutoAssociate]);

  return {
    isAssociated,
    isLoading,
    isCreating,
    error,
    checkAssociation,
    checkAssociationAndAutoAssociate,
  };
};
